/**
 * PlanPieces — "the pieces of this plan, in plain words".
 *
 * Owner, 24 Sep 2026: "when you select explain this, it actually doesn't explain the strategy (i.e.
 * we reference into super — downsizer contribution) but we don't actually explain what this is
 * anywhere — this would be foreign concept to many clients … think about how this is explained in
 * a way that is as easy as possible, doesn't create too much visual load on the screen."
 *
 * So: one row per piece the plan is made of. Each row is the piece's everyday name, the amount
 * where there is one, and ONE short line saying what it is and what the means tests do with it.
 * Nothing else is on the row. Where the vetted glossary (awiseKnowledgeBase) holds a fuller
 * explanation, the row ends with a dotted "what's this?" that opens it in the centred pop-up — the
 * same device the rest of the app uses — so the depth is there for the reader who wants it and
 * costs the others nothing.
 *
 * The rows are DATA, built by each module from the strategy it is explaining (rvPlanPieces.ts for
 * the village), so the same card can sit in every module's "Understanding your plan".
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from './NarrativePanel.tsx';
import { lookupGlossaryShort } from '../../constants/awiseKnowledgeBase.ts';

export interface PlanPiece {
    key: string;
    /** The everyday name — "Downsizer contribution into super (Cameron)". */
    name: string;
    /** One short line: what it is, and what the means tests do with it. */
    what: string;
    /** The dollars committed to it, where there are any. */
    amount?: number | null;
    /** "a year" for a yearly figure such as a daily fee or rent. */
    amountSuffix?: string;
    /** A term the vetted glossary explains; the row offers "what's this?" only when it does. */
    glossaryTerm?: string;
}

export function PlanPiecesCard({ pieces, className = '', plain = false }: { pieces: PlanPiece[]; className?: string; /** No card and no gradient: one eyebrow line, for a surface that already frames it (the cause→effect Explain this, 17 Oct 2026). */ plain?: boolean }) {
    const rows = pieces.filter((p) => p && p.name && p.what);
    if (rows.length === 0) return null;
    return (
        <div className={plain ? `pt-1 ${className}` : `rounded-2xl border border-slate-200/70 bg-white p-5 shadow-card ${className}`}>
            {plain ? (
                <p className="mb-3 flex items-center gap-3 text-[12.5px] font-semibold text-brand-navy">
                    <span className="shrink-0">The pieces of this plan, and how the means tests treat each</span>
                    <span className="h-px flex-1 bg-slate-200" aria-hidden />
                </p>
            ) : (
                <>
                    <h4 className="text-lg font-bold">
                        <span className="text-brand-navy">The pieces of this plan</span>
                    </h4>
                    <p className="mt-0.5 mb-3 text-xs text-slate-500">What each part is, in plain words, and how the means tests treat it.</p>
                </>
            )}
            <ul className="divide-y divide-slate-100">
                {rows.map((p) => {
                    const hasGlossary = !!p.glossaryTerm && !!lookupGlossaryShort(p.glossaryTerm);
                    return (
                        <li key={p.key} className="py-2.5 first:pt-0 last:pb-0">
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-[14px] font-semibold text-slate-900">{p.name}</span>
                                {p.amount != null && Number.isFinite(p.amount) && p.amount > 0 && (
                                    <span className="shrink-0 text-[14px] font-semibold tabular-nums text-slate-900">
                                        {formatCurrency(p.amount)}
                                        {p.amountSuffix ? <span className="ml-1 text-[11px] font-medium text-slate-500">{p.amountSuffix}</span> : null}
                                    </span>
                                )}
                            </div>
                            <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">
                                {p.what}
                                {hasGlossary && (
                                    <>
                                        {' '}
                                        <JargonTerm term={p.glossaryTerm as string} expandAs="dialog" label="what's this?" />
                                    </>
                                )}
                            </p>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default PlanPiecesCard;
